<template>
  <div>
    <settings-group
      title="Review Team"
      description="Manage who can access the review"
    >
      <lead-review-member-setting
        :lead-reviewer="leadReviewer"
        :reviewers="reviewers"
        :editable="userIsLeadOrSuperUser"
        @change-lead-reviewer="onChangeLeadReviewer"
      />
      <v-divider />
      <review-members-setting
        :researcher-search-input.sync="searchInput"
        :researcher-search-results="researcherSearchResults"
        :lead-reviewer="leadReviewer"
        :reviewers="reviewers"
        :invites="invites"
        :editable="userIsLeadOrSuperUser"
        @invite="onInviteResearcher"
        @invite-by-email="onInviteByEmail"
        @delete="onDeleteResearcher"
        @revoke-invite="$emit('revoke-invite', $event)"
      />
      <v-divider />
    </settings-group>
  </div>
</template>

<script>
import SettingsGroup from './SettingsGroup';
import LeadReviewMemberSetting from './LeadReviewMemberSetting';
import ReviewMembersSetting from './ReviewMembersSetting';
import { ReviewMember, Researcher } from '@/models';
import { ReviewMemberRole } from '@/enums';
import { typeHelpers } from '@/modules';

export default {
  name: 'ReviewMemberSettingsPanel',
  components: {
    SettingsGroup,
    LeadReviewMemberSetting,
    ReviewMembersSetting,
  },
  props: {
    researcherSearchInput: {
      type: String,
      default: null,
    },
    researcherSearchResults: {
      type: Array,
      default: () => [],
      validator: (val) => typeHelpers.isArrayOfType(val, Researcher),
    },
    leadReviewer: {
      type: ReviewMember,
      required: true,
    },
    reviewers: {
      type: Array,
      default: () => [],
    },
    viewers: {
      type: Array,
      default: () => [],
    },
    invites: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    userIsLeadOrSuperUser: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'update:researcherSearchInput',
    'invite-member',
    'update-member',
    'remove-member',
    'revoke-invite',
  ],
  data: () => ({ addingNewReviewer: false, addingNewViewer: false }),
  computed: {
    searchInput: {
      get: function () {
        return this.researcherSearchInput;
      },
      set: function (value) {
        this.$emit('update:researcherSearchInput', value);
      },
    },
    hasReviewers() {
      return this.reviewers && this.reviewers.length > 0;
    },
    hasInvites() {
      return this.invites && this.invites.length > 0;
    },
    hasViewers() {
      return this.viewers && this.viewers.length > 0;
    },
    usedAddresses() {
      var inviteeAddresses = this.invites.map(
        (invite) => invite.emailAddress
      );
      var reviewerAddresses = this.reviewers.map(
        (reviewer) => reviewer.researcher.email
      );
      return inviteeAddresses.concat(reviewerAddresses);
    },
    nonMemberResearchers() {
      return this.researcherSearchResults.filter(
        (researcher) =>
          this.leadReviewer.researcher.id !== researcher.id &&
          !this.reviewers.some(
            (reviewer) => reviewer.researcher.id === researcher.id
          ) &&
          !this.viewers.some(
            (viewer) => viewer.researcher.id === researcher.id
          )
      );
    },
  },
  methods: {
    onChangeLeadReviewer(leadReviewer) {
      const newLeadReviewer = new ReviewMember({
        ...leadReviewer,
        memberRole: ReviewMemberRole.LEAD_REVIEWER,
      });
      this.$emit('update-member', newLeadReviewer);
    },
    onInviteResearcher(researcher) {
      this.onInviteByEmail(researcher.email);
    },
    onInviteByEmail(emailAddress) {
      this.$emit('invite-member', {
        emailAddress,
        memberRole: ReviewMemberRole.REVIEWER,
      });
    },
    onDeleteResearcher(researcher) {
      const reviewMember = this.reviewers.find(
        (reviewer) => reviewer.researcher.id === researcher.id
      );
      this.$emit('remove-member', reviewMember);
    },
  },
};
</script>
